import {
  area,
  curveBasis,
  extent,
  line,
  max,
  min,
  scaleBand,
  scaleLinear,
  scaleUtc,
} from 'd3';
import isNumber from 'lodash/isNumber';
import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReplotChartNames } from '../../constants';
import { FormatType } from '../../types';
import {
  getFormatter,
  getMaxTextWidth,
  useChartTheme,
  useResizeObserver,
  xAxisTickCount,
  yAxisTickCount,
} from '../../utilities';
import { type DateFormat, formatDate } from '../../utilities/date-formatter';
import {
  SENTIMENT_NEGATIVE_COLOR_DARK,
  SENTIMENT_NEGATIVE_COLOR_LIGHT,
  SENTIMENT_POSITIVE_COLOR,
} from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { tickAnchor } from '../../utilities/tick-anchor';
import { getTimeAxisTicks } from '../../utilities/time-axis-ticks';
import { AXIS_NO_WRAP_HEIGHT, AxisLabel } from '../axis/axis-label';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import {
  getCursorPosition,
  PlotTooltip,
  TooltipHoverLine,
  type TooltipPosition,
  useTooltipController,
} from '../plot-tooltip';
import styles from './volume-chart.module.css';
import { createIndexedVolumeData, getWeekData } from './volume-chart.utils';
import {
  DEFAULT_VOLUME_LABELS,
  type VolumeSeriesLabels,
  VolumeTooltip,
  type VolumeTooltipProps,
} from './volume-tooltip';

export type VolumeChartData = {
  id: string;
  label: string;
  values: {
    date: string;
    value: number;
  }[];
  color?: string;
};

export interface VolumeChartProps {
  data: VolumeChartData[];
  format?: FormatType;
  dateFormat?: DateFormat;
  loading?: boolean;
  color?: string;
  seriesLabels?: Partial<VolumeSeriesLabels>;
  onHoverChange?: (date: Date | null) => void;
  xTickCount?: number;
  fontSize?: number;
}

type VolumeDatumWithDate = {
  date: Date;
  value: number;
};

const Y_AXIS_MAX_WIDTH = 50;
const X_AXIS_HEIGHT = 12;

const CHART_PADDING_LEFT = 6;
const CHART_PADDING_TOP = 8;
const CHART_PADDING_BOTTOM = 8;
const BAR_PADDING = 0.2;

export const VolumeChart = ({
  data,
  format = FormatType.INTEGER,
  color,
  dateFormat = 'day',
  loading = false,
  seriesLabels,
  onHoverChange,
  xTickCount: xTickCountOverride,
  fontSize = 11,
}: VolumeChartProps) => {
  const { containerRef, width, height } = useResizeObserver();
  const chartRef = useRef<SVGGElement>(null);
  const [highlightedDate, setHighlightedDate] = useState<number | null>(null);

  const { theme, getPlotColor } = useChartTheme();
  const isDark = theme === 'dark';
  const addedBarColor = SENTIMENT_POSITIVE_COLOR;
  const removedBarColor = isDark
    ? SENTIMENT_NEGATIVE_COLOR_DARK
    : SENTIMENT_NEGATIVE_COLOR_LIGHT;
  const lineColor = color ?? getPlotColor(0);
  const areaFill = 'url(#area-gradient)';

  // Data transformation
  const indexedData = useMemo(() => createIndexedVolumeData(data), [data]);

  const seriesData = useMemo(() => {
    const transformMapToPoints = (
      map: Map<number, number>
    ): VolumeDatumWithDate[] =>
      Array.from(map.entries()).map(([time, value]) => {
        const date = new Date(time);
        return {
          date,
          value,
        };
      });

    return {
      active: transformMapToPoints(indexedData.active),
      new: transformMapToPoints(indexedData.new),
      removed: transformMapToPoints(indexedData.removed),
    };
  }, [indexedData]);

  // Y Scale
  const formatValue = getFormatter(format);
  const plotHeight = Math.max(
    0,
    height - X_AXIS_HEIGHT - CHART_PADDING_TOP - CHART_PADDING_BOTTOM
  );
  const yTickCount = yAxisTickCount(plotHeight);

  const yDomain = useMemo(
    () => [
      min(seriesData.removed.map((d) => -d.value)) || 0,
      max([
        ...seriesData.active.map((d) => d.value),
        ...seriesData.new.map((d) => d.value),
      ]) || 0,
    ],
    [seriesData]
  );

  const yScale = scaleLinear().domain(yDomain).range([plotHeight, 0]);

  const yTicks = yScale
    ? yScale
        .ticks(yTickCount)
        .filter((tick) =>
          format === FormatType.INTEGER ? Number.isInteger(tick) : true
        )
        .map((tick) => ({
          value: tick,
          label: formatValue(tick),
        }))
    : [];

  const yAxisWidth = yTicks
    ? Math.min(
        getMaxTextWidth({ texts: yTicks.map((t) => t.label) }),
        Y_AXIS_MAX_WIDTH
      )
    : Y_AXIS_MAX_WIDTH;

  const plotWidth = Math.max(0, width - yAxisWidth - CHART_PADDING_LEFT);

  // X Scale
  const xTickCount = xTickCountOverride ?? xAxisTickCount(plotWidth);
  const xDomain = extent(indexedData.weekDates);
  const xScale = scaleUtc()
    .domain(xDomain as [Date, Date])
    .range([0, plotWidth]);

  const xTicks = getTimeAxisTicks({
    scale: xScale,
    tickCount: xTickCount,
    fontSize,
  });

  // Bar scale
  const xScaleBar = scaleBand()
    .domain(indexedData.weekDates.map((d) => d.getTime().toString()))
    .range([0, Math.max(0, plotWidth)])
    .padding(BAR_PADDING);

  const barWidth = Math.max(0, xScaleBar.bandwidth());

  // Area and line paths
  const areaPath = area<VolumeDatumWithDate>()
    .x((d) => xScale(d.date))
    .y0(yScale(0))
    .y1((d) => yScale(d.value))
    .curve(curveBasis)(seriesData.active);

  const linePath = line<VolumeDatumWithDate>()
    .x((d) => xScale(d.date))
    .y((d) => yScale(d.value))
    .curve(curveBasis)(seriesData.active);

  // Tooltip
  const [tooltipRows, setTooltipRows] = useState<VolumeTooltipProps | null>(
    null
  );
  const [hoverLineX, setHoverLineX] = useState<number | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const hoveredDate = useRef<Date | null>(null);

  const handleTooltipMove = (
    position: [number, number],
    event: ReactMouseEvent<SVGGElement>
  ) => {
    if (!xScale) return;
    setCursor(getCursorPosition(event));

    const xPos = position[0];
    const nearestWeekIndex = Math.floor(
      xPos / (plotWidth / indexedData.weekDates.length)
    );
    const bisectDate = indexedData.weekDates[nearestWeekIndex];

    if (!bisectDate) return;

    const weekData = getWeekData(indexedData, bisectDate.getTime());
    setTooltipRows({
      title: formatDate(bisectDate, dateFormat),
      ...weekData,
    });

    const barX = xScaleBar(bisectDate.getTime().toString());
    const snappedX =
      barX !== undefined ? barX + xScaleBar.bandwidth() / 2 : xPos;

    setHoverLineX(snappedX);
    setHighlightedDate(bisectDate.getTime());
    if (hoveredDate.current?.getTime() !== bisectDate.getTime()) {
      hoveredDate.current = bisectDate;
      onHoverChange?.(bisectDate);
    }
  };

  const tooltipController = useTooltipController({
    onHover: handleTooltipMove,
  });

  // Reset highlight when tooltip is hidden
  useEffect(() => {
    if (!tooltipController.isVisible) {
      setHighlightedDate(null);
      setHoverLineX(null);
      setCursor(null);
      setTooltipRows(null);
      if (hoveredDate.current !== null) {
        hoveredDate.current = null;
        onHoverChange?.(null);
      }
    }
  }, [tooltipController.isVisible, onHoverChange]);

  useEffect(
    () => () => {
      if (hoveredDate.current === null) return;
      hoveredDate.current = null;
      onHoverChange?.(null);
    },
    [onHoverChange]
  );

  const hasData =
    data.length &&
    data.some((d) => d.values.filter((v) => isNumber(v.value)).length >= 2);

  return (
    <div
      ref={containerRef}
      className={styles.container}
      data-testid={plotTestId(ReplotChartNames.VolumeChart)}
    >
      <PlotLoadWrapper loading={loading} noData={!hasData}>
        <svg width={width} height={height}>
          <defs>
            <linearGradient id="area-gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity={0.4} />
              <stop offset="81.25%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <g transform={`translate(0, ${CHART_PADDING_TOP})`}>
            {/* Y Axis and Grid Lines */}
            <g data-testid="plot-y-axis">
              {yTicks.map((tick, i) => (
                <g key={i}>
                  <AxisLabel
                    y={yScale(tick.value)}
                    width={yAxisWidth}
                    availableHeight={AXIS_NO_WRAP_HEIGHT}
                    label={tick.label}
                  />
                  <line
                    x1={yAxisWidth + CHART_PADDING_LEFT}
                    x2={yAxisWidth + CHART_PADDING_LEFT + plotWidth}
                    y1={yScale(tick.value)}
                    y2={yScale(tick.value)}
                    className={styles.gridLine}
                  />
                </g>
              ))}
            </g>

            {/* X Axis and Grid Lines */}
            <g
              data-testid="plot-x-axis"
              className={styles.xAxis}
              transform={`translate(${yAxisWidth + CHART_PADDING_LEFT}, 0)`}
            >
              {xTicks.map((tick, i) => (
                <g key={i}>
                  <text
                    x={xScale(tick.value)}
                    y={plotHeight + CHART_PADDING_BOTTOM + 9}
                    fontSize={fontSize}
                    style={{
                      textAnchor: tickAnchor(
                        i,
                        xTicks.length,
                        xScale(tick.value),
                        plotWidth
                      ),
                    }}
                  >
                    {tick.label}
                  </text>
                </g>
              ))}
            </g>

            {/* Chart Area */}
            <g
              id="chart"
              ref={chartRef}
              transform={`translate(${yAxisWidth + CHART_PADDING_LEFT}, 0)`}
              {...tooltipController.gProps}
              className={styles.chart}
            >
              {tooltipController.isVisible && hoverLineX !== null && (
                <TooltipHoverLine x={hoverLineX} height={plotHeight} />
              )}
              <path
                d={areaPath || ''}
                fill={areaFill}
                data-testid="volume-chart-area"
              />
              <path
                d={linePath || ''}
                fill="none"
                stroke={lineColor}
                strokeWidth={2}
                className="line-path"
              />
              {seriesData.new.map((d, i) => (
                <g key={i}>
                  <rect
                    x={xScaleBar(d.date.getTime().toString())}
                    y={yScale(d.value)}
                    width={barWidth}
                    height={Math.abs(yScale(0) - yScale(d.value))}
                    fill={addedBarColor}
                    className={`${styles.bar} added-bar ${
                      highlightedDate === d.date.getTime()
                        ? styles.highlighted
                        : ''
                    }`}
                  />
                </g>
              ))}
              {seriesData.removed.map((d, i) => (
                <g key={i}>
                  <rect
                    x={xScaleBar(d.date.getTime().toString())}
                    y={yScale(0)}
                    width={barWidth}
                    height={Math.abs(yScale(-d.value) - yScale(0))}
                    fill={removedBarColor}
                    className={`${styles.bar} removed-bar ${
                      highlightedDate === d.date.getTime()
                        ? styles.highlighted
                        : ''
                    }`}
                  />
                </g>
              ))}
              <rect
                x={0}
                y={0}
                width={plotWidth}
                height={plotHeight}
                fill="transparent"
              />
            </g>
          </g>
        </svg>

        {cursor && (
          <PlotTooltip
            isVisible={tooltipController.isVisible}
            x={cursor.x}
            y={cursor.y}
            space="viewport"
          >
            {tooltipRows && (
              <VolumeTooltip
                {...tooltipRows}
                format={format}
                labels={{ ...DEFAULT_VOLUME_LABELS, ...seriesLabels }}
                showActive={indexedData.active.size > 0}
              />
            )}
          </PlotTooltip>
        )}
      </PlotLoadWrapper>
    </div>
  );
};
